import { Filter } from "@/shared/lib/filter/filter";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { Address } from "@/services/backend/addresses/types/address";
import { Representative } from "@/services/backend/representatives/types/representative";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Reason } from "@bitory-gmbh/nidavellir";
import {
  downloadResponseHandler,
  previewResponseHandler,
} from "@/services/backend/_lib/pdf-helpers";
import { Lst } from "@/shared/lib/lst/lst";
import { backendApi } from "@/services/backend/api";
import { Offer } from "@/services/backend/vbs/offers/types/offer";

const offerApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    vbsOfferCreate: build.mutation<Offer, CreateRequest>({
      query: (request) => ({
        url: `/vbs/offer`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsOffer", id: request.id },
        "VbsOffer",
      ],
    }),
    vbsOfferCreateFromOld: build.mutation<Offer, CreateFromOldRequest>({
      query: (request) => ({
        url: `/vbs/offer/create-from-old`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [{ type: "VbsOffer", id: "LIST" }, "VbsOffer"],
    }),
    vbsOfferCreateFromCalculation: build.mutation<
      Offer,
      CreateFromCalculationRequest
    >({
      query: (request) => ({
        url: `/vbs/offer/create-from-calculation`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [{ type: "VbsOffer", id: "LIST" }, "VbsOffer"],
    }),
    vbsOfferShow: build.query<Offer, ShowRequest>({
      query: (request) => ({
        url: `/vbs/offer`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "VbsOffer", id: result.id }] : [],
    }),
    vbsOfferList: build.query<Lst<Offer>, Partial<ListRequest>>({
      query: (request) => ({
        url: `/vbs/offer/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "VbsOffer"),
    }),
    vbsOfferUpdateDate: build.mutation<Offer, UpdateDateRequest>({
      query: (request) => ({
        url: `/vbs/offer/offer-date`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsOffer", id: request.id },
        "VbsOffer",
      ],
    }),
    vbsOfferUpdateTreatmentPriceUseCalculation: build.mutation<
      Offer,
      UpdateTreatmentUseCalculationRequest
    >({
      query: (request) => ({
        url: `/vbs/offer/treatment-price-use-calculation`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsOffer", id: request.id },
        "VbsOffer",
        "VbsPricing",
        "VbsPricingByArea",
      ],
    }),
    vbsOfferUpdateIncidentalCostUseCalculation: build.mutation<
      Offer,
      UpdateIncidentalCostUseCalculationRequest
    >({
      query: (request) => ({
        url: `/vbs/offer/incidental-cost-use-calculation`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsOffer", id: request.id },
        "VbsOffer",
        "VbsPricing",
        "VbsPricingByArea",
      ],
    }),
    vbsOfferUpdatePostalAddress: build.mutation<
      Offer,
      UpdatePostalAddressRequest
    >({
      query: (request) => ({
        url: `/vbs/offer/postal-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsOffer", id: request.id },
        "VbsOffer",
      ],
    }),
    vbsOfferUpdateServiceAddress: build.mutation<
      Offer,
      UpdateServiceAddressRequest
    >({
      query: (request) => ({
        url: `/vbs/offer/service-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsOffer", id: request.id },
        "VbsOffer",
      ],
    }),
    vbsOfferUpdateRepresentative: build.mutation<
      Offer,
      UpdateRepresentativeRequest
    >({
      query: (request) => ({
        url: `/vbs/offer/representative`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsOffer", id: request.id },
        "VbsOffer",
      ],
    }),
    vbsOfferPdfPreview: build.query<{ objectURL: string }, PDFRequest>({
      query: (request) => ({
        url: `/vbs/offer`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: previewResponseHandler,
        // IMPORTANT. Since this query will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      providesTags: (_r, _e, request) => [{ type: "VbsOffer", id: request.id }],
    }),
    vbsOfferPdfDownload: build.mutation<
      { filename: string; objectURL: string },
      PDFRequest
    >({
      query: (request) => ({
        url: `/vbs/offer`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // IMPORTANT. Since this mutation will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
    }),
    vbsOfferSetImmutable: build.mutation<Offer, SetImmutableRequest>({
      query: (request) => ({
        url: `/vbs/offer/set-immutable`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsOffer", id: request.id },
        "VbsOffer",
        "NumbersDocumentNumber",
      ],
    }),
    vbsOfferDelete: build.mutation<Offer, DeleteRequest>({
      query: (request) => ({
        url: `/vbs/offer`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsOffer", id: request.id },
        "VbsOffer",
      ],
    }),
  }),
});

export const {
  useVbsOfferCreateMutation,
  useVbsOfferCreateFromOldMutation,
  useVbsOfferCreateFromCalculationMutation,
  useVbsOfferShowQuery,
  useVbsOfferListQuery,
  useVbsOfferUpdateDateMutation,
  useVbsOfferUpdateTreatmentPriceUseCalculationMutation,
  useVbsOfferUpdateIncidentalCostUseCalculationMutation,
  useVbsOfferUpdatePostalAddressMutation,
  useVbsOfferUpdateServiceAddressMutation,
  useVbsOfferUpdateRepresentativeMutation,
  useVbsOfferSetImmutableMutation,
  useVbsOfferPdfDownloadMutation,
  useVbsOfferPdfPreviewQuery,
  useVbsOfferDeleteMutation,
} = offerApi;

export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateFromOldRequest {
  oldOfferId: string;
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateFromCalculationRequest {
  calculationId: string;
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters {
  orderBy?: OrdrBy<string>[];
  offerDate?: Filter<string>;
  immutable?: Filter<boolean>;
  limit?: number;
  offset?: number;
}

export interface DeleteRequest {
  id: string;
}

export interface UpdateDateRequest {
  id: string;
  date: string;
}

export interface UpdateTreatmentUseCalculationRequest {
  id: string;
  value: boolean;
}

export interface UpdateIncidentalCostUseCalculationRequest {
  id: string;
  value: boolean;
}

export interface UpdatePostalAddressRequest {
  id: string;
  postalAddress: Address;
}

export interface UpdateServiceAddressRequest {
  id: string;
  serviceAddress: Address;
}

export interface UpdateRepresentativeRequest {
  id: string;
  representative: Representative;
}

export enum OfferDocument {
  ALL = "all",
  OFFER = "offer",
  ORDER = "order",
  TREATMENT = "treatment",
  TREATMENT_BY_AREA = "treatment-by-area",
}

export interface PDFRequest {
  id: string;
  document: OfferDocument;
}

export interface SetImmutableRequest {
  id: string;
  reason: Reason;
  setBy: string;
}

export enum OfferImmutabilityReason {
  SetByUser = "set_by_user",
}
